// src/views/test.ts
import { html, type TemplateResult, nothing } from 'lit-html';
import { renderProgressBar, bindProgressBar } from '../components/progress-bar.js';
import { showToast } from '../components/toast.js';
import questionnaireARaw from '../data/questionnaire-a.json';
import questionnaireBRaw from '../data/questionnaire-b.json';
import type { Store } from '../modules/store.js';
import type { Router } from '../modules/router.js';
import type { Questionnaire } from '../types/index.js';

const QUESTIONNAIRES: Record<'A' | 'B', Questionnaire> = {
  A: questionnaireARaw as unknown as Questionnaire,
  B: questionnaireBRaw as unknown as Questionnaire,
};

interface Encouragement {
  emoji: string;
  text: string;
}

// 阶段性鼓励文字
function getEncouragement(idx: number, totalAnswered: number): Encouragement {
  if (idx === 0) return { emoji: '✨', text: '开始你的学习风格探索吧' };
  if (idx === 15) return { emoji: '🎯', text: '最后一题,即将看到你的画像' };
  if (idx >= 12) return { emoji: '🔥', text: `临近完成 — 已答 ${totalAnswered} 题,再坚持一下` };
  if (idx >= 8) return { emoji: '⚡', text: `已经过半了 — 已答 ${totalAnswered} 题,保持节奏` };
  if (idx >= 4) return { emoji: '👍', text: `节奏不错 — 已答 ${totalAnswered} 题,继续` };
  return { emoji: '🌿', text: '慢慢来,选最贴近你平时做法的' };
}

interface RenderTestParams {
  store: Store;
}

export function renderTest({ store }: RenderTestParams): TemplateResult {
  if (store.state.status !== 'in_progress' || !store.state.questionnaire) {
    return html`<div class="container"><p>未在测试中,<a href="#/intro">从这里开始</a>。</p></div>`;
  }
  const data = QUESTIONNAIRES[store.state.questionnaire];
  const idx = store.state.currentIndex;
  const q = data.questions[idx];
  if (!q) {
    return html`<div class="container"><p>题目数据异常,<a href="#/intro">重新开始</a>。</p></div>`;
  }
  const answered = store.state.answers
    .map((a, i) => a.selected.length > 0 ? i : -1)
    .filter(i => i >= 0);
  const currentAnswer = store.state.answers[idx];
  const selectedSet = new Set(currentAnswer ? currentAnswer.selected : []);
  const encouragement = getEncouragement(idx, answered.length);

  return html`
    <div class="container">
      <div class="encouragement">
        <span class="encouragement-emoji">${encouragement.emoji}</span>
        <span class="encouragement-text">${encouragement.text}</span>
      </div>

      ${renderProgressBar({ total: 16, current: idx, answered })}

      <div class="question-card">
        <div class="qheader qmeta">
          <div class="qnumber-badge">
            <span class="qnumber-label">第</span>
            <span class="qnumber-value">${idx + 1}</span>
            <span class="qnumber-total">/ 16 题</span>
          </div>
          <div class="qheader-right">
            <span class="qbank-tag">题库 ${store.state.questionnaire}</span>
            ${q.scenario_en ? html`<button class="link" id="toggle-en">查看英文原文</button>` : nothing}
          </div>
        </div>

        <h2 class="qtext">${q.scenario_zh}</h2>
        ${q.scenario_en ? html`<p class="qtext-en hidden" id="qtext-en">${q.scenario_en}</p>` : nothing}

        <div class="options" data-multi>
          ${q.options.map(o => {
            const isSelected = selectedSet.has(o.id);
            return html`
              <label class=${`opt ${isSelected ? 'opt-selected' : ''}`} data-opt=${o.id}>
                <span class="opt-letter">${o.id}</span>
                <input type="checkbox" ?checked=${isSelected} class="visually-hidden" />
                <span class="opt-body">
                  <span class="opt-text">${o.text_zh}</span>
                  ${o.text_en ? html`<span class="opt-en hidden">${o.text_en}</span>` : nothing}
                </span>
                <span class="opt-tick" aria-hidden="true">✓</span>
              </label>
            `;
          })}
        </div>

        <div class="qfooter">
          <span class="qselected" id="qselected">已选 ${selectedSet.size} 项</span>
          <details class="qhint-detail">
            <summary>为什么可以多选?</summary>
            <p>VARK 是<strong>多选问卷</strong>。Fleming 设计时认为人在不同情境下的偏好可能并存,因此鼓励你勾出所有贴近你做法的选项,系统会按官方阶梯计分法综合评估你的偏好维度。</p>
          </details>
        </div>
      </div>

      <nav class="qnav">
        <button class="btn btn-secondary" id="prev" ?disabled=${idx === 0}>← 上一题</button>
        <span class="qnav-hint" aria-hidden="true">数字键 ${q.options.map(o => o.id).join('/')} 选选项 · ← → 翻页</span>
        ${idx < 15 ? html`<button class="btn btn-primary" id="next">下一题 →</button>` : nothing}
        ${idx === 15 ? html`<button class="btn btn-primary btn-submit" id="submit">✓ 提交并查看你的画像</button>` : nothing}
      </nav>
    </div>
  `;
}

export function bindTest(root: HTMLElement, store: Store, router: Router, onSubmit: () => void): void {
  if (!store.state.questionnaire) return;
  const idx = store.state.currentIndex;
  const data = QUESTIONNAIRES[store.state.questionnaire];
  const q = data.questions[idx];
  if (!q) return;
  const validKeys = q.options.map(o => o.id);

  function updateSelectedCount(): void {
    const cur = store.state.answers[idx];
    if (!cur) return;
    const count = cur.selected.length;
    const el = root.querySelector('#qselected');
    if (el) el.textContent = `已选 ${count} 项`;
  }

  root.querySelectorAll<HTMLInputElement>('.opt input[type="checkbox"]').forEach(input => {
    input.addEventListener('change', () => {
      const label = input.closest<HTMLElement>('.opt');
      if (!label) return;
      const optId = label.dataset.opt;
      if (!optId) return;
      const ans = store.state.answers[idx];
      if (!ans) return;
      const cur = new Set(ans.selected);
      if (input.checked) cur.add(optId);
      else cur.delete(optId);
      store.setAnswer(idx + 1, [...cur]);
      label.classList.toggle('opt-selected', input.checked);
      updateSelectedCount();
    });
  });

  bindProgressBar(root, (jumpIdx: number) => {
    store.setCurrentIndex(jumpIdx);
    router.navigate('/test');
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  });

  function goPrev(): void {
    if (idx === 0) return;
    const ans = store.state.answers[idx];
    if (!ans || ans.selected.length === 0) {
      showToast('请至少选 1 项再继续', { type: 'warn' });
      return;
    }
    store.setCurrentIndex(idx - 1);
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  }

  function goNext(): void {
    if (idx >= 15) return;
    const ans = store.state.answers[idx];
    if (!ans || ans.selected.length === 0) {
      showToast('请至少选 1 项再继续', { type: 'warn' });
      return;
    }
    store.setCurrentIndex(idx + 1);
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  }

  function trySubmit(): void {
    if (!store.isComplete()) {
      showToast('还有题目未作答,请先答完所有题', { type: 'warn' });
      return;
    }
    onSubmit();
  }

  root.querySelector<HTMLButtonElement>('#prev')?.addEventListener('click', goPrev);
  root.querySelector<HTMLButtonElement>('#next')?.addEventListener('click', goNext);
  root.querySelector<HTMLButtonElement>('#submit')?.addEventListener('click', trySubmit);

  root.querySelector<HTMLButtonElement>('#toggle-en')?.addEventListener('click', () => {
    root.querySelector('#qtext-en')?.classList.toggle('hidden');
    root.querySelectorAll('.opt-en').forEach(el => el.classList.toggle('hidden'));
  });

  // 键盘快捷键
  function handleKeydown(e: KeyboardEvent): void {
    // 表单/输入框中或按住 meta 键时不接管
    const target = e.target as HTMLElement | null;
    const tag = (target?.tagName || '').toUpperCase();
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;

    const key = e.key.toUpperCase();
    if (validKeys.includes(key)) {
      e.preventDefault();
      const input = root.querySelector<HTMLInputElement>(`.opt[data-opt="${key}"] input`);
      if (input) {
        input.checked = !input.checked;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }
    if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); return; }
    if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); return; }
    if (e.key === 'Enter' && idx === 15) { e.preventDefault(); trySubmit(); return; }
  }
  document.addEventListener('keydown', handleKeydown);
  // 视图切换时清理(observe 直到 root 被替换)
  const cleanup = (): void => {
    document.removeEventListener('keydown', handleKeydown);
    window.removeEventListener('hashchange', cleanup);
  };
  window.addEventListener('hashchange', cleanup, { once: true });
}
